import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiDollarSign, FiGlobe, FiZap, FiShare2, FiMail, FiLink, FiNavigation, FiFilter, FiTool } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '/api/admin' : 'http://localhost:5000/api/admin';

const SOURCE_COLORS = {
  'Paid': '#EF4444', 'Organic Search': '#10B981', 'AI Search': '#8B5CF6',
  'Social': '#F59E0B', 'Email': '#3B82F6', 'Referral': '#06B6D4',
  'Direct': '#6B7280', 'Unknown': '#D1D5DB'
};

const TYPE_ICONS = {
  'Paid': FiDollarSign, 'Organic Search': FiGlobe, 'AI Search': FiZap,
  'Social': FiShare2, 'Email': FiMail, 'Referral': FiLink, 'Direct': FiNavigation
};

export default function CreditBuySource() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const token = localStorage.getItem('rg_token');
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const res = await axios.get(`${API_URL}/analytics/credit-source?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setData(res.data.data);
      else throw new Error(res.data.error);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]); // eslint-disable-line

  const runBackfill = async () => {
    try {
      setBackfilling(true); setBackfillResult(null);
      const token = localStorage.getItem('rg_token');
      const res = await axios.post(`${API_URL}/analytics/backfill-attribution`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setBackfillResult(res.data.data.message);
        fetchData(true);
      }
    } catch (err) {
      setBackfillResult('Error: ' + (err.response?.data?.error || err.message));
    } finally { setBackfilling(false); }
  };

  const getTypeCount = (type) => data?.summary?.byType?.find(t => t.type === type)?.count || 0;
  const getTypeRevenue = (type) => data?.summary?.byType?.find(t => t.type === type)?.revenue || 0;

  const filteredPurchases = (data?.recentPurchases || []).filter(b => filterType === 'all' || b.source_type === filterType);

  // Pie data for visual breakdown
  const pieData = data?.summary?.byType?.map(t => ({
    name: t.type, value: t.count, revenue: t.revenue, color: SOURCE_COLORS[t.type] || '#6B7280'
  })) || [];
  const totalForPie = pieData.reduce((s, p) => s + p.value, 0);

  if (loading) return <div style={styles.loadingContainer}><div style={styles.spinner} /></div>;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.title}>Credit Buy Source</h2>
          <p style={styles.subtitle}>Track where your credit purchases come from — Paid, Organic, AI Search, Social, and more</p>
        </div>
        <div style={styles.headerActions}>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.dateInput} />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.dateInput} />
          <button onClick={runBackfill} disabled={backfilling} style={styles.btnOutline}>
            <FiTool size={14} /> {backfilling ? 'Reclassifying...' : 'Fix Unknown Sources'}
          </button>
          <button onClick={() => fetchData(true)} disabled={refreshing} style={styles.iconBtn}>
            <FiRefreshCw size={16} />
          </button>
        </div>
      </div>

      {backfillResult && <div style={styles.infoAlert}>{backfillResult} <button onClick={() => setBackfillResult(null)} style={styles.closeBtn}>×</button></div>}
      {error && <div style={styles.errorAlert}>{error}</div>}

      {/* Summary Cards - Row 1 */}
      <div style={styles.cardGrid4}>
        <StatCard title="Total Purchases" value={data?.summary?.totalPurchases || 0} subtitle={`$${(data?.summary?.totalRevenue || 0).toFixed(2)} revenue`} icon={FiDollarSign} gradient="linear-gradient(135deg, #1E40AF, #3B82F6)" loading={refreshing} />
        <StatCard title="Paid" value={getTypeCount('Paid')} subtitle={`$${getTypeRevenue('Paid').toFixed(2)} revenue`} icon={FiDollarSign} color="#EF4444" loading={refreshing} />
        <StatCard title="Organic Search" value={getTypeCount('Organic Search')} subtitle={`$${getTypeRevenue('Organic Search').toFixed(2)} revenue`} icon={FiGlobe} color="#10B981" loading={refreshing} />
        <StatCard title="AI Search" value={getTypeCount('AI Search')} subtitle={`$${getTypeRevenue('AI Search').toFixed(2)} revenue`} icon={FiZap} color="#8B5CF6" loading={refreshing} />
      </div>

      {/* Summary Cards - Row 2 */}
      <div style={styles.cardGrid4}>
        <StatCard title="Social" value={getTypeCount('Social')} subtitle={`$${getTypeRevenue('Social').toFixed(2)} revenue`} icon={FiShare2} color="#F59E0B" loading={refreshing} />
        <StatCard title="Referral" value={getTypeCount('Referral')} subtitle={`$${getTypeRevenue('Referral').toFixed(2)} revenue`} icon={FiLink} color="#06B6D4" loading={refreshing} />
        <StatCard title="Email" value={getTypeCount('Email')} subtitle={`$${getTypeRevenue('Email').toFixed(2)} revenue`} icon={FiMail} color="#3B82F6" loading={refreshing} />
        <StatCard title="Direct" value={getTypeCount('Direct')} subtitle={`$${getTypeRevenue('Direct').toFixed(2)} revenue`} icon={FiNavigation} color="#6B7280" loading={refreshing} />
      </div>

      {/* Charts Row: Source Breakdown + Platform Bar */}
      <div style={styles.twoCol}>
        {/* Source Type Breakdown */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Source Type Breakdown</h3>
          {pieData.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              {pieData.map((p, i) => {
                const pct = totalForPie > 0 ? (p.value / totalForPie * 100) : 0;
                return (
                  <div key={i} style={styles.breakdownRow}>
                    <div style={{ ...styles.breakdownDot, background: p.color }} />
                    <span style={styles.breakdownLabel}>{p.name}</span>
                    <div style={styles.breakdownBarOuter}>
                      <div style={{ ...styles.breakdownBar, width: `${pct}%`, background: p.color }} />
                    </div>
                    <span style={styles.breakdownValue}>{p.value} ({pct.toFixed(0)}%)</span>
                    <span style={styles.breakdownRevenue}>${p.revenue.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          ) : <div style={styles.emptyState}>No data yet</div>}
        </div>

        {/* Purchases by Platform */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Purchases by Platform</h3>
          {(data?.byPlatform || []).length > 0 ? (
            <div style={{ marginTop: 16 }}>
              {(data.byPlatform).map((p, i) => {
                const maxCount = Math.max(...data.byPlatform.map(x => x.count));
                const pct = maxCount > 0 ? (p.count / maxCount * 100) : 0;
                return (
                  <div key={i} style={styles.breakdownRow}>
                    <span style={{ ...styles.breakdownLabel, minWidth: 140 }}>{p.platform}</span>
                    <span style={{ ...styles.badge, background: `${SOURCE_COLORS[p.type] || '#6B7280'}15`, color: SOURCE_COLORS[p.type] || '#6B7280', fontSize: 10 }}>{p.type}</span>
                    <div style={styles.breakdownBarOuter}>
                      <div style={{ ...styles.breakdownBar, width: `${pct}%`, background: SOURCE_COLORS[p.type] || '#6B7280', borderRadius: '0 4px 4px 0' }} />
                    </div>
                    <span style={styles.breakdownValue}>{p.count}</span>
                    <span style={styles.breakdownRevenue}>${p.revenue.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          ) : <div style={styles.emptyState}>No data yet</div>}
        </div>
      </div>

      {/* Campaign Performance */}
      {(data?.byCampaign || []).length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Campaign Performance</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Campaign</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Purchases</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.byCampaign.map((c, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}>{c.source}</td>
                  <td style={styles.td}><code style={styles.code}>{c.campaign}</code></td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{c.count}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>${c.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Landing Pages */}
      {(data?.byLandingPage || []).length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Top Landing Pages</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Landing Page</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Purchases</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.byLandingPage.map((l, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}><code style={styles.code}>{l.landingPage}</code></td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{l.count}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>${l.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Purchases Table */}
      <div style={styles.card}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>Recent Credit Purchases by Source</h3>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
            <option value="all">All Sources</option>
            <option value="Paid">Paid</option>
            <option value="Organic Search">Organic Search</option>
            <option value="AI Search">AI Search</option>
            <option value="Social">Social</option>
            <option value="Email">Email</option>
            <option value="Referral">Referral</option>
            <option value="Direct">Direct</option>
          </select>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Plan</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Credits</th>
              <th style={styles.th}>Source</th>
              <th style={styles.th}>Campaign</th>
              <th style={styles.th}>Landing Page</th>
            </tr>
          </thead>
          <tbody>
            {filteredPurchases.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...styles.td, textAlign: 'center', padding: 32 }}>
                  <span style={{ color: '#9CA3AF' }}>
                    {(data?.recentPurchases || []).length === 0
                      ? 'No purchases with attribution data yet. New purchases will appear here with source tracking.'
                      : 'No purchases match the selected filter.'}
                  </span>
                </td>
              </tr>
            ) : filteredPurchases.map((p, i) => (
              <tr key={i} style={styles.tr}>
                <td style={styles.td}><span style={{ fontSize: 12 }}>{new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span></td>
                <td style={styles.td}><strong>{p.user}</strong></td>
                <td style={styles.td}><span style={{ fontSize: 12, textTransform: 'capitalize' }}>{p.planId}</span></td>
                <td style={styles.td}>${p.amount.toFixed(2)}</td>
                <td style={styles.td}>{p.credits.toLocaleString()}</td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    background: `${SOURCE_COLORS[p.source_type] || '#6B7280'}15`,
                    color: SOURCE_COLORS[p.source_type] || '#6B7280',
                    border: `1px solid ${SOURCE_COLORS[p.source_type] || '#6B7280'}30`
                  }}>
                    {p.source_type} - {p.source_platform}
                  </span>
                </td>
                <td style={styles.td}><code style={styles.code}>{p.utm_campaign || '-'}</code></td>
                <td style={styles.td}><code style={styles.code}>{p.landing_page || '-'}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================
// Stat Card
// ============================================
function StatCard({ title, value, subtitle, icon: Icon, color, gradient, loading }) {
  return (
    <div style={{ ...styles.statCard, background: gradient || 'var(--bg-secondary)', border: gradient ? 'none' : '1px solid var(--border)' }}>
      <div style={styles.statCardInner}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: gradient ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)', marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: gradient ? 'white' : 'var(--text-primary)' }}>{loading ? '...' : value}</div>
          {subtitle && <div style={{ fontSize: 11, color: gradient ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)', marginTop: 2 }}>{subtitle}</div>}
        </div>
        <div style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: gradient ? 'rgba(255,255,255,0.2)' : `${color}15` }}>
          <Icon size={22} style={{ color: gradient ? 'white' : color }} />
        </div>
      </div>
    </div>
  );
}

// ============================================
// Styles
// ============================================
const styles = {
  container: { maxWidth: 1400, margin: '0 auto' },
  loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  spinner: { width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' },
  headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  dateInput: { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13, width: 150 },
  iconBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: 6, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' },
  btnOutline: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' },

  errorAlert: { padding: '10px 16px', background: '#FEE2E2', color: '#DC2626', borderRadius: 8, marginBottom: 16, fontSize: 13 },
  infoAlert: { padding: '10px 16px', background: '#DBEAFE', color: '#1E40AF', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', color: 'inherit', fontSize: 18, cursor: 'pointer', padding: '0 4px' },

  cardGrid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 },
  statCard: { borderRadius: 12, padding: 20, transition: 'transform 0.2s' },
  statCardInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },

  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 },
  cardHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
  cardTitle: { fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' },

  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20, marginBottom: 0 },

  breakdownRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  breakdownDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  breakdownLabel: { fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500, minWidth: 100 },
  breakdownBarOuter: { flex: 1, height: 8, borderRadius: 4, background: 'var(--bg-primary)' },
  breakdownBar: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  breakdownValue: { fontSize: 13, fontWeight: 600, minWidth: 60, textAlign: 'right' },
  breakdownRevenue: { fontSize: 12, color: 'var(--text-secondary)', minWidth: 60, textAlign: 'right' },

  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  filterSelect: { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer' },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' },
  tr: { borderBottom: '1px solid var(--border)' },
  td: { padding: '10px 12px', fontSize: 13 },
  code: { fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' },

  emptyState: { textAlign: 'center', padding: 32, color: 'var(--text-secondary)', fontSize: 13 }
};
